import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Building2,
  Users,
  Palette,
  Sparkles,
  Plus,
  Trash2,
  Wand2,
  Edit3,
  X,
} from 'lucide-react';
import { DEFAULT_OFFICE_SETTINGS } from './OfficeSettings';
import {
  CHARACTER_CATEGORIES,
  ART_STYLES,
  OFFICE_ENVIRONMENTS,
  THEME_PRESETS,
  CHANNELS,
} from './agentcorpThemes';

// Steps for the wizard
const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Building2 },
  { id: 'theme', title: 'Theme', icon: Palette },
  { id: 'agents', title: 'Agents', icon: Users },
  { id: 'finish', title: 'Finish', icon: Sparkles },
];

/**
 * Step 1: Welcome - Name your office
 */
const WelcomeStep = ({ onNext, officeName, setOfficeName }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
    <div className="w-20 h-20 bg-gradient-to-br from-[#ff4d4d] to-[#ff6b6b] rounded-2xl flex items-center justify-center mb-6 animate-float">
      <Building2 className="w-10 h-10 text-white" />
    </div>
    <h2 className="text-2xl font-bold text-white mb-2">Create Your AI Office</h2>
    <p className="text-white/60 mb-8 max-w-md">
      Set up a virtual workspace where your AI agents serve customers. Watch them work in real-time with fun animations!
    </p>

    <div className="w-full max-w-sm">
      <label className="block text-sm font-medium text-white/70 mb-2 text-left">
        Name Your Office
      </label>
      <input
        type="text"
        value={officeName}
        onChange={(e) => setOfficeName(e.target.value)}
        placeholder="e.g., Dream Team HQ, Sales Squad..."
        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d] transition-colors"
      />
    </div>

    <button
      onClick={onNext}
      disabled={!officeName.trim()}
      className={`
        mt-8 flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all
        ${officeName.trim()
          ? 'bg-[#ff4d4d] text-white hover:bg-[#ff6666]'
          : 'bg-white/10 text-white/30 cursor-not-allowed'
        }
      `}
    >
      Get Started
      <ArrowRight className="w-5 h-5" />
    </button>
  </div>
);

/**
 * Step 2: Choose Theme - Character category, art style, environment
 */
const ThemeStep = ({
  selectedCategory,
  setSelectedCategory,
  selectedArtStyle,
  setSelectedArtStyle,
  selectedEnvironment,
  setSelectedEnvironment,
  onNext,
  onBack,
}) => {
  const [activeTab, setActiveTab] = useState('presets'); // presets, custom

  return (
    <div className="min-h-[400px]">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Choose Your Theme</h2>
        <p className="text-white/60">Select a preset or customize your office style.</p>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-2 mb-6 justify-center">
        <button
          onClick={() => setActiveTab('presets')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'presets'
              ? 'bg-[#ff4d4d] text-white'
              : 'bg-white/5 text-white/60 hover:bg-white/10'
          }`}
        >
          Quick Presets
        </button>
        <button
          onClick={() => setActiveTab('custom')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'custom'
              ? 'bg-[#ff4d4d] text-white'
              : 'bg-white/5 text-white/60 hover:bg-white/10'
          }`}
        >
          Customize
        </button>
      </div>

      {activeTab === 'presets' && (
        <div className="grid grid-cols-3 gap-3 max-h-[300px] overflow-y-auto p-1">
          {THEME_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => {
                setSelectedCategory(preset.category);
                setSelectedArtStyle(preset.artStyle);
                setSelectedEnvironment(preset.background);
              }}
              className={`
                p-3 rounded-xl border-2 transition-all text-left
                ${selectedCategory === preset.category && selectedArtStyle === preset.artStyle
                  ? 'border-[#ff4d4d] bg-[#ff4d4d]/10'
                  : 'border-white/10 hover:border-white/20'
                }
              `}
            >
              <div className={`w-full h-12 rounded-lg mb-2 ${preset.preview}`} />
              <p className="text-xs font-semibold text-white truncate">{preset.name}</p>
              <p className="text-[10px] text-white/50 truncate">{preset.description}</p>
            </button>
          ))}
        </div>
      )}

      {activeTab === 'custom' && (
        <div className="space-y-6 max-h-[300px] overflow-y-auto p-1">
          {/* Character Category */}
          <div>
            <h3 className="text-sm font-medium text-white/70 mb-3">Character Type</h3>
            <div className="grid grid-cols-3 gap-2">
              {Object.values(CHARACTER_CATEGORIES).map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`
                    p-2 rounded-lg border-2 transition-all text-center
                    ${selectedCategory === cat.id
                      ? 'border-[#ff4d4d] bg-[#ff4d4d]/10'
                      : 'border-white/10 hover:border-white/20'
                    }
                  `}
                >
                  <span className="text-2xl">{cat.icon}</span>
                  <p className="text-xs text-white mt-1">{cat.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Art Style */}
          <div>
            <h3 className="text-sm font-medium text-white/70 mb-3">Art Style</h3>
            <div className="grid grid-cols-4 gap-2">
              {Object.values(ART_STYLES).map((style) => (
                <button
                  key={style.id}
                  onClick={() => setSelectedArtStyle(style.id)}
                  className={`
                    p-2 rounded-lg border-2 transition-all text-center
                    ${selectedArtStyle === style.id
                      ? 'border-[#ff4d4d] bg-[#ff4d4d]/10'
                      : 'border-white/10 hover:border-white/20'
                    }
                  `}
                >
                  <div className={`w-full h-6 rounded mb-1 ${style.preview}`} />
                  <p className="text-[10px] text-white truncate">{style.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Environment */}
          <div>
            <h3 className="text-sm font-medium text-white/70 mb-3">Office Environment</h3>
            <div className="grid grid-cols-4 gap-2">
              {Object.values(OFFICE_ENVIRONMENTS).map((env) => (
                <button
                  key={env.id}
                  onClick={() => setSelectedEnvironment(env.id)}
                  className={`
                    p-2 rounded-lg border-2 transition-all text-center
                    ${selectedEnvironment === env.id
                      ? 'border-[#ff4d4d] bg-[#ff4d4d]/10'
                      : 'border-white/10 hover:border-white/20'
                    }
                  `}
                >
                  <span className="text-xl">{env.icon}</span>
                  <p className="text-[10px] text-white mt-1 truncate">{env.name}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          className="flex items-center gap-2 px-6 py-2 rounded-xl font-medium bg-[#ff4d4d] text-white hover:bg-[#ff6666] transition-all"
        >
          Continue
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

/**
 * Agent Edit Modal with Channel Assignment
 */
const AgentEditModal = ({ agent, onSave, onClose, category }) => {
  const [name, setName] = useState(agent?.name || '');
  const [selectedEmoji, setSelectedEmoji] = useState(agent?.emoji || '🤖');
  const [selectedChannels, setSelectedChannels] = useState(agent?.channels || ['all']);

  const characters = CHARACTER_CATEGORIES[category]?.characters || [];
  const channelOptions = Object.values(CHANNELS);

  const toggleChannel = (channelId) => {
    if (channelId === 'all') {
      setSelectedChannels(['all']);
    } else {
      setSelectedChannels(prev => {
        const withoutAll = prev.filter(c => c !== 'all');
        if (withoutAll.includes(channelId)) {
          const result = withoutAll.filter(c => c !== channelId);
          return result.length === 0 ? ['all'] : result;
        } else {
          return [...withoutAll, channelId];
        }
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#0d1424] border border-white/10 rounded-2xl p-6 w-full max-w-md animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            {agent ? 'Edit Agent' : 'Add Agent'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg">
            <X className="w-5 h-5 text-white/60" />
          </button>
        </div>

        {/* Name Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            Agent Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Lily, Max, Alex..."
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#ff4d4d]"
            autoFocus
          />
        </div>

        {/* Character Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            Choose Character
          </label>
          <div className="grid grid-cols-6 gap-2 max-h-24 overflow-y-auto">
            {characters.map((char) => (
              <button
                key={char.id}
                onClick={() => setSelectedEmoji(char.emoji)}
                className={`p-2 rounded-lg border-2 transition-all text-2xl
                  ${selectedEmoji === char.emoji
                    ? 'border-[#ff4d4d] bg-[#ff4d4d]/10'
                    : 'border-white/10 hover:border-white/20'
                  }`}
                title={char.name}
              >
                {char.emoji}
              </button>
            ))}
          </div>
        </div>

        {/* Channel Assignment */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-white/70 mb-2">
            Assigned Channels
          </label>
          <div className="flex flex-wrap gap-2">
            {channelOptions.map((channel) => (
              <button
                key={channel.id}
                onClick={() => toggleChannel(channel.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                  ${selectedChannels.includes(channel.id)
                    ? 'text-white'
                    : 'bg-white/5 text-white/50 hover:bg-white/10'
                  }`}
                style={selectedChannels.includes(channel.id) ? { backgroundColor: channel.color + '30', borderColor: channel.color, border: '1px solid' } : {}}
              >
                <span>{channel.icon}</span>
                <span>{channel.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-white/60 hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (name.trim()) {
                onSave({
                  ...agent,
                  id: agent?.id || `agent_${Date.now()}`,
                  name: name.trim(),
                  emoji: selectedEmoji,
                  channels: selectedChannels,
                });
                onClose();
              }
            }}
            disabled={!name.trim()}
            className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all
              ${name.trim()
                ? 'bg-[#ff4d4d] text-white hover:bg-[#ff6666]'
                : 'bg-white/10 text-white/30 cursor-not-allowed'
              }`}
          >
            {agent ? 'Save' : 'Add Agent'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Step 3: Configure Agents with custom names
 */
const AgentsStep = ({ agents, setAgents, category, onNext, onBack }) => {
  const [editingAgent, setEditingAgent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const characters = CHARACTER_CATEGORIES[category]?.characters || [];

  const addAgent = (agent) => {
    setAgents([...agents, agent]);
  };

  const updateAgent = (updatedAgent) => {
    setAgents(agents.map(a => a.id === updatedAgent.id ? updatedAgent : a));
  };

  const removeAgent = (id) => {
    setAgents(agents.filter(a => a.id !== id));
  };

  // Quick add from template
  const quickAddAgent = (char) => {
    const existingCount = agents.filter(a => a.emoji === char.emoji).length;
    const name = existingCount > 0 ? `${char.name} ${existingCount + 1}` : char.name;
    addAgent({
      id: `agent_${Date.now()}`,
      name: name.split(' ')[0], // Just use first name part
      emoji: char.emoji,
      color: char.color,
    });
  };

  return (
    <div className="min-h-[400px]">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Add Your Agents</h2>
        <p className="text-white/60">Create agents with custom names and characters.</p>
      </div>

      {/* Quick Add Characters */}
      <div className="mb-4">
        <h3 className="text-sm font-medium text-white/70 mb-2">Quick Add</h3>
        <div className="flex flex-wrap gap-2">
          {characters.slice(0, 6).map((char) => (
            <button
              key={char.id}
              onClick={() => quickAddAgent(char)}
              className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors group"
            >
              <span className="text-xl">{char.emoji}</span>
              <Plus className="w-4 h-4 text-white/40 group-hover:text-[#ff4d4d] transition-colors" />
            </button>
          ))}
        </div>
      </div>

      {/* Custom Agent Button */}
      <button
        onClick={() => setShowAddModal(true)}
        className="w-full mb-4 px-4 py-3 bg-white/5 border border-dashed border-white/20 rounded-xl text-white/60 hover:bg-white/10 hover:border-[#ff4d4d]/50 hover:text-white transition-all flex items-center justify-center gap-2"
      >
        <Plus className="w-5 h-5" />
        Add Custom Agent
      </button>

      {/* Added Agents */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-white/70 mb-2">
          Your Team ({agents.length})
        </h3>
        {agents.length === 0 ? (
          <div className="text-center py-8 bg-white/5 rounded-xl border border-white/10">
            <span className="text-4xl mb-2 block">
              {CHARACTER_CATEGORIES[category]?.icon || '🤖'}
            </span>
            <p className="text-sm text-white/40">No agents added yet</p>
            <p className="text-xs text-white/30 mt-1">Click characters above to add them</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center justify-between p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{agent.emoji}</span>
                  <span className="text-sm font-medium text-white">{agent.name}</span>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => setEditingAgent(agent)}
                    className="p-1.5 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeAgent(agent.id)}
                    className="p-1.5 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={agents.length === 0}
          className={`
            flex items-center gap-2 px-6 py-2 rounded-xl font-medium transition-all
            ${agents.length > 0
              ? 'bg-[#ff4d4d] text-white hover:bg-[#ff6666]'
              : 'bg-white/10 text-white/30 cursor-not-allowed'
            }
          `}
        >
          Continue
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* Edit Modal */}
      {editingAgent && (
        <AgentEditModal
          agent={editingAgent}
          category={category}
          onSave={updateAgent}
          onClose={() => setEditingAgent(null)}
        />
      )}

      {/* Add Modal */}
      {showAddModal && (
        <AgentEditModal
          agent={null}
          category={category}
          onSave={addAgent}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </div>
  );
};

/**
 * Step 4: Finish - Review and create
 */
const FinishStep = ({
  officeName,
  agents,
  category,
  artStyle,
  environment,
  onBack,
  onCreate,
}) => {
  const categoryData = CHARACTER_CATEGORIES[category];
  const styleData = ART_STYLES[artStyle];
  const envData = OFFICE_ENVIRONMENTS[environment];

  return (
    <div className="min-h-[400px] flex flex-col items-center justify-center text-center">
      <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-400 rounded-2xl flex items-center justify-center mb-6 animate-pulse-glow">
        <Check className="w-10 h-10 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Ready to Launch!</h2>
      <p className="text-white/60 mb-6 max-w-md">
        Your office is configured. Review the details below.
      </p>

      {/* Summary Card */}
      <div className="w-full max-w-md bg-white/5 rounded-2xl p-4 mb-6 text-left border border-white/10">
        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-white/10">
          <div className="w-12 h-12 bg-gradient-to-br from-[#ff4d4d] to-[#ff6b6b] rounded-xl flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-semibold text-white">{officeName}</p>
            <p className="text-xs text-white/50">{agents.length} agents</p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-2 bg-white/5 rounded-lg">
            <span className="text-xl">{categoryData?.icon}</span>
            <p className="text-[10px] text-white/50 mt-1">{categoryData?.name}</p>
          </div>
          <div className="text-center p-2 bg-white/5 rounded-lg">
            <div className={`w-6 h-6 mx-auto rounded ${styleData?.preview}`} />
            <p className="text-[10px] text-white/50 mt-1">{styleData?.name}</p>
          </div>
          <div className="text-center p-2 bg-white/5 rounded-lg">
            <span className="text-xl">{envData?.icon}</span>
            <p className="text-[10px] text-white/50 mt-1">{envData?.name}</p>
          </div>
        </div>

        {/* Agent Preview */}
        <div className="flex flex-wrap gap-2">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="flex items-center gap-2 px-2 py-1 bg-white/5 rounded-lg"
            >
              <span>{agent.emoji}</span>
              <span className="text-xs text-white">{agent.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onCreate}
          className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#ff4d4d] to-[#ff6b6b] text-white rounded-xl font-semibold hover:from-[#ff6666] hover:to-[#ff8080] transition-all animate-pulse-glow"
        >
          <Wand2 className="w-5 h-5" />
          Create Office
        </button>
      </div>
    </div>
  );
};

/**
 * OfficeCreationWizard - Multi-step wizard for creating a new office
 */
const OfficeCreationWizard = ({ isOpen, onClose, onCreate }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [officeName, setOfficeName] = useState('');
  const [agents, setAgents] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('robots');
  const [selectedArtStyle, setSelectedArtStyle] = useState('modern');
  const [selectedEnvironment, setSelectedEnvironment] = useState('corporate');

  const handleCreate = () => {
    const officeConfig = {
      name: officeName,
      agents: agents.map(a => ({
        id: a.id,
        name: a.name,
        emoji: a.emoji,
        icon: a.icon,
        color: a.color,
        channels: a.channels || ['all'],
        activeChats: 0,
        responsesCount: 0,
      })),
      theme: {
        category: selectedCategory,
        artStyle: selectedArtStyle,
        environment: selectedEnvironment,
      },
      settings: {
        ...DEFAULT_OFFICE_SETTINGS,
        style: selectedArtStyle,
      },
      createdAt: new Date().toISOString(),
    };

    onCreate(officeConfig);
    onClose();
  };

  const goNext = () => setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
  const goBack = () => setCurrentStep(prev => Math.max(prev - 1, 0));

  // Reset agents when category changes
  const handleCategoryChange = (cat) => {
    if (cat !== selectedCategory) {
      setAgents([]); // Clear agents when switching categories
    }
    setSelectedCategory(cat);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Wizard */}
      <div className="relative w-full max-w-2xl bg-[#0d1424] border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-white/10 bg-white/5">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`
                  flex items-center gap-2 px-3 py-1.5 rounded-full transition-all
                  ${index === currentStep
                    ? 'bg-[#ff4d4d] text-white'
                    : index < currentStep
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-white/5 text-white/40'
                  }
                `}>
                  {index < currentStep ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <step.icon className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium hidden sm:block">{step.title}</span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={`
                    w-8 h-0.5 mx-2
                    ${index < currentStep ? 'bg-emerald-500' : 'bg-white/10'}
                  `} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {currentStep === 0 && (
            <WelcomeStep
              onNext={goNext}
              officeName={officeName}
              setOfficeName={setOfficeName}
            />
          )}
          {currentStep === 1 && (
            <ThemeStep
              selectedCategory={selectedCategory}
              setSelectedCategory={handleCategoryChange}
              selectedArtStyle={selectedArtStyle}
              setSelectedArtStyle={setSelectedArtStyle}
              selectedEnvironment={selectedEnvironment}
              setSelectedEnvironment={setSelectedEnvironment}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {currentStep === 2 && (
            <AgentsStep
              agents={agents}
              setAgents={setAgents}
              category={selectedCategory}
              onNext={goNext}
              onBack={goBack}
            />
          )}
          {currentStep === 3 && (
            <FinishStep
              officeName={officeName}
              agents={agents}
              category={selectedCategory}
              artStyle={selectedArtStyle}
              environment={selectedEnvironment}
              onBack={goBack}
              onCreate={handleCreate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

OfficeCreationWizard.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

export default OfficeCreationWizard;
